import React, { FC, lazy, useEffect, useContext, useRef, useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import { ApiUtil, ErrorPage } from '@everfi/reactifi';
import { ClassRouterProps } from '@/teacher/Classes';
import { ExploreCoursesRouterProps } from '@/teacher/ExploreCourses';
import { InviteATeacherRouterProps } from '@/teacher/InviteATeacher';
import { GuestSpeakersRouterProps } from '@/teacher/GuestSpeakers';
import { MyProfileRouterProps } from '@/teacher/MyProfile';
import environment from '@/environment';
import { AppContext } from '@/layout/Main';
import { Analytics, Context } from './common/routingTypes';
import CustomFavIcon from '../lib/CustomFavIcon';
import { useSalesforceChat } from '@/lib/useSalesforceChat';
import { useAnalytics } from '@lib/analytics/useAnalytics';
import { StudentsListProps } from './Students/components/StudentsList';

const Dashboard = lazy(() => import('./dashboard/index'));
const Classes = lazy(() => import('./Classes/index'));
const ExploreCourses = lazy(() => import('./ExploreCourses/index'));
const InviteATeacher = lazy(() => import('./InviteATeacher/index'));
const GuestSpeakers = lazy(() => import('./GuestSpeakers/index'));
const MyProfile = lazy(() => import('./MyProfile/index'));
const MyStudents = lazy(() => import('./Students/index'));

type TeacherRouterProps = ClassRouterProps &
  ExploreCoursesRouterProps &
  InviteATeacherRouterProps &
  StudentsListProps &
  GuestSpeakersRouterProps &
  MyProfileRouterProps & { errors: string[] };

const TeacherApp: FC<TeacherRouterProps> = props => {
  const context = useContext(AppContext) as Context;
  const analyticsFired = useRef(false);
  const [analyticsData, setAnalyticsData] = useState<{ id: string; accountId: string; env: string } | null>(null);

  useEffect(() => {
    if (analyticsFired.current) return;
    analyticsFired.current = true;

    async function getAnalytics() {
      const url = `${environment.apiBasePath.HOMEROOM}/analytics`;
      const analytics: Analytics = await ApiUtil.loadExternalData(url, context.accessToken, true);

      const { id, application, school } = analytics;
      if (id && application && school) {
        setAnalyticsData({
          id: id.toString(),
          accountId: school.id.toString(),
          env: analytics.application.env,
        });
      }
    }

    getAnalytics();
  }, [context.accessToken]);

  useSalesforceChat({
    firstName: context?.user.first_name,
    lastName: context?.user.last_name,
    email: context?.user.email,
    enabled: context?.user?.flippers?.k12_teacher_salesforce_chat,
  });

  useAnalytics({
    userId: analyticsData?.id,
    accountId: analyticsData?.accountId,
    // @ts-ignore (environment is dynamically generated)
    apiKey: environment.PENDO_ANALYTICS_API_KEY_HOMEROOM,
    isFoundry: false,
  });

  return (
    <>
      <CustomFavIcon />
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="explore_courses/*" element={<ExploreCourses {...props} />} />
        <Route path="my_classes/*" element={<Classes {...props} />} />
        <Route path="my_students" element={<MyStudents {...props} />} />
        <Route path="guest_speakers/*" element={<GuestSpeakers {...props} />} />
        <Route path="invite_a_teacher" element={<InviteATeacher {...props} />} />
        <Route path="my_profile/*" element={<MyProfile {...props} />} />
        <Route path="*" element={<ErrorPage {...props} title="Page Not Found" />} />
      </Routes>
    </>
  );
};

export default TeacherApp;
